import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnInit, Output } from '@angular/core';

// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { TravelCardType } from '@ridango/components/lib/medium';

import {
    LocalWebTicket,
    UserTicketAction,
    UserTicketActionsEnum,
    UserTicketActionCarrier,
    AppStartupService
} from '@ridango/core';

import momenttz from 'moment-timezone';

@Component({
    selector: 'ridango-base-ticket',
    templateUrl: './base-ticket.component.html'
})
export class BaseTicketComponent implements OnInit {
    openTickets = false;
    actionsOpened = false;

    @Input() ticket: LocalWebTicket;
    @Input() locale = 'en'; //  2 letter language code, lets default to en (english)
    @Input() cards = []; // cards that the ticket is being bought to. currently klaipeda-corporate only.

    @Input() actions: UserTicketAction[] = [];
    @Input() useInputActions = false;
    @Input() showActionDropdownAlways = false;

    @Input() transportDayEndPrevDate = false;

    @Input() hasZoneModal = false;

    @Output() requestDeleteTicket = new EventEmitter<LocalWebTicket>();
    @Output() requestRemoveCard = new EventEmitter<any>();
    @Output() requestDisplayQR = new EventEmitter<any>();
    @Output() requestPauseTicket = new EventEmitter<LocalWebTicket>();
    @Output() requestReactivateTicket = new EventEmitter<LocalWebTicket>();
    @Output() requestRenewTicket = new EventEmitter<LocalWebTicket>();

    @Output() requestTicketAction: EventEmitter<UserTicketActionCarrier> = new EventEmitter<UserTicketActionCarrier>();

    @Output() requestShowZoneModal: EventEmitter<any> = new EventEmitter<any>();

    dayNames = [
        'LOCAL_CALENDAR_SUNDAY_ddd',
        'LOCAL_CALENDAR_MONDAY_ddd',
        'LOCAL_CALENDAR_TUESDAY_ddd',
        'LOCAL_CALENDAR_WEDNESDAY_ddd',
        'LOCAL_CALENDAR_THURSDAY_ddd',
        'LOCAL_CALENDAR_FRIDAY_ddd',
        'LOCAL_CALENDAR_SATURDAY_ddd'
    ];

    constructor(
        @Inject('ENV') public env: any,
        public eRef: ElementRef,
        public appStartupService: AppStartupService
    ) {}

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
    ngOnInit(): void {}

    toggleOpenTickets() {
        this.openTickets = !this.openTickets;
    }

    actionClick(action, id = null) {
        switch (action.type) {
            case UserTicketActionsEnum.QR:
                this.requestDisplayQR.emit(this.ticket);
                break;
            case UserTicketActionsEnum.DELETE:
                this.requestDeleteTicket.emit(this.ticket);
                break;
            case UserTicketActionsEnum.REMOVE:
                // can be card id or doc_num
                this.requestRemoveCard.emit({ ticket: this.ticket, id: id });
                break;
        }
    }

    toggleActions() {
        this.actionsOpened = !this.actionsOpened;
    }

    actionClicked(action: UserTicketAction, ticket: LocalWebTicket) {
        this.requestTicketAction.emit(new UserTicketActionCarrier(ticket, action));
    }

    @HostListener('document:click', ['$event']) onClickOut(event) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.actionsOpened = false;
        }
    }
    getMedium() {
        if (this.isVoucher()) {
            // Translate tag in the specific ticket component
            return false;
        }

        if (this.ticket.related_card_object && this.ticket.related_card_object.card_nr) {
            return this.ticket.related_card_object.card_nr;
        }

        if (this.ticket.medium_object && Object.keys(this.ticket.medium_object).length !== 0) {
            return this.ticket.medium_object.card_number
                ? this.ticket.medium_object.card_number
                : this.ticket.medium_object.email;
        }
        if (this.ticket.related_card_object) {
            return this.ticket.related_card_object.card_nr;
        }
        if (this.ticket.notifications && this.ticket.visual_card_id) {
            return this.ticket.visual_card_id;
        }
        if (this.ticket.card_id && !this.ticket.visual_card_id && this.ticket.card_type !== TravelCardType.Email) {
            if (this.ticket.card_type === TravelCardType.Barcode) {
                return this.ticket.buyer_card_id;
            }
            return this.ticket.card_id;
        }
        if (this.ticket.visual_card_id && !this.ticket.email) {
            return this.ticket.visual_card_id;
        }
        if (this.ticket.email) {
            return this.ticket.card_type === TravelCardType.Email ? this.ticket.email : this.ticket.card_id;
        }

        return false;
    }

    getMediumType() {
        if (this.isVoucher()) {
            return 'TICKET_SUMMARY_V2_MEDIUM';
        }

        if (this.ticket.card_type === TravelCardType.EstId) {
            return 'CARD_NUMBER';
        }

        if (this.ticket.related_card_object && this.ticket.related_card_object.card_nr) {
            return 'TICKET_SUMMARY_V2_TRAVELLER_CARD';
        }

        if (this.ticket.medium_object && Object.keys(this.ticket.medium_object).length !== 0) {
            return this.ticket.medium_object.card_number
                ? 'TICKET_SUMMARY_V2_TRAVELLER_CARD'
                : 'TICKET_SUMMARY_V2_TRAVELLER_EMAIL';
        }

        if (this.ticket.card_type === TravelCardType.Email) {
            return 'TICKET_SUMMARY_V2_TRAVELLER_EMAIL';
        }

        return 'TICKET_SUMMARY_V2_TRAVELLER_CARD';
    }

    isVoucher() {
        return (this.ticket.medium_object && this.ticket.medium_object.type === 'S') || this.ticket.card_type === 'S';
    }

    formatDate(time, isEnding = false) {
        let intermediateTime = time;
        if (this.transportDayEndPrevDate && isEnding) {
            intermediateTime = momenttz
                .tz(time, this.appStartupService.getTimezone())
                .subtract(1, 'd')
                .hours(23)
                .minutes(59)
                .seconds(59);
        }

        return momenttz
            .tz(intermediateTime, this.appStartupService.getTimezone())
            .format(this.env.PRIMARY_DATE_FORMAT_MOMENT);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formatDateForHoursTicket(time, isEnding = false) {
        return momenttz
            .tz(time, this.appStartupService.getTimezone())
            .format(this.env.PRIMARY_DATE_FORMAT_MOMENT + ' HH:mm');
    }

    getDayName(time, isEnding = false) {
        let intermediateTime = time;
        if (this.transportDayEndPrevDate && isEnding) {
            intermediateTime = momenttz
                .tz(time, this.appStartupService.getTimezone())
                .subtract(1, 'd')
                .hours(23)
                .minutes(59)
                .seconds(59);
        }

        return this.dayNames[momenttz.tz(intermediateTime, this.appStartupService.getTimezone()).day()];
    }

    getDayNameForHoursTicket(time) {
        return this.dayNames[momenttz.tz(time, this.appStartupService.getTimezone()).day()];
    }

    showZoneModal() {
        this.requestShowZoneModal.emit();
    }
}
